import React, { useState, useRef, useEffect } from "react"
import CMSRender from "aldoo-ra/CMS/cms-render"
import Typography from "aldoo-ra/Typography"
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import { motion, AnimatePresence } from "framer-motion"
import DynamicSVG from "aldoo-ra/DynamicSVG"
import { twMerge } from "tailwind-merge"

// Update NavigationButton to respect disabled state
const NavigationButton = ({ direction, onClick, disabled }) => (
  <motion.button
    whileTap={{ scale: disabled ? 1 : 0.95 }}
    onClick={onClick}
    disabled={disabled}
    className={twMerge(
      "w-[70px] h-[70px] rounded-full border border-gray-300 flex items-center justify-center transition-colors",
      disabled
        ? "opacity-50 cursor-not-allowed"
        : "hover:bg-gray-100 cursor-pointer"
    )}
  >
    {direction === "prev" ? (
      <ChevronLeftIcon className="w-[36px] h-[36px]" />
    ) : (
      <ChevronRightIcon className="w-[36px] h-[36px]" />
    )}
  </motion.button>
)

const Carousel = ({ items, itemRender, setControls }) => {
  const [isAnimating, setIsAnimating] = useState(false)
  const [dimensions, setDimensions] = useState({ cardWidth: 0, cardGap: 0 })
  const [isInitialized, setIsInitialized] = useState(false)

  const [cards, setCards] = useState(() => {
    const firstCard = items[0]
    const lastCard = items[items.length - 1]
    return [lastCard, ...items, firstCard]
  })

  const containerRef = useRef(null)
  const cardRefs = useRef([])
  const animationRef = useRef(null)
  const observerRef = useRef(null)

  // Initialize dimension measurements
  useEffect(() => {
    const measureDimensions = () => {
      if (cardRefs.current[0]) {
        const cardElement = cardRefs.current[0]
        const cardRect = cardElement.getBoundingClientRect()

        // Get the actual gap from computed styles
        const containerStyle = window.getComputedStyle(containerRef.current)
        const gap = parseInt(containerStyle.gap) || 0

        setDimensions({
          cardWidth: cardRect.width,
          cardGap: gap,
        })

        // Initialize position after getting dimensions
        if (!isInitialized && containerRef.current) {
          const slideDistance = cardRect.width + gap
          containerRef.current.style.transform = `translateX(${-slideDistance}px)`
          setIsInitialized(true)
        }
      }
    }

    // Setup ResizeObserver
    observerRef.current = new ResizeObserver(() => {
      measureDimensions()
    })

    // Observe container and first card
    if (containerRef.current && cardRefs.current[0]) {
      observerRef.current.observe(containerRef.current)
      observerRef.current.observe(cardRefs.current[0])
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [isInitialized])

  const moveCard = (direction) => {
    setCards((prev) => {
      if (direction === "next") {
        const newCards = [...prev]
        newCards.shift()
        const lastIndex = items.findIndex(
          (item) => item._id === newCards[newCards.length - 1]._id
        )
        let nextIndex = lastIndex + 1
        if (nextIndex > items.length - 1) nextIndex = 0
        newCards.push(items[nextIndex])
        return newCards
      } else {
        const newCards = [...prev]
        newCards.pop()
        const firstIndex = items.findIndex(
          (item) => item._id === newCards[0]._id
        )
        let prevIndex = firstIndex - 1
        if (prevIndex < 0) prevIndex = items.length - 1
        newCards.unshift(items[prevIndex])
        return newCards
      }
    })
  }

  const resetContainer = (direction) => {
    moveCard(direction)

    if (containerRef.current) {
      const slideDistance = dimensions.cardWidth + dimensions.cardGap
      containerRef.current.style.transition = "none"
      containerRef.current.style.transform = `translateX(${-slideDistance}px)`
      containerRef.current.offsetHeight // Force reflow
      containerRef.current.style.transition = "transform 500ms linear"
    }
  }

  const slide = (direction) => {
    if (isAnimating || !containerRef.current || !dimensions.cardWidth) return

    setIsAnimating(true)
    const slideDistance = dimensions.cardWidth + dimensions.cardGap
    const currentTransform = -slideDistance
    const targetTransform =
      direction === "next"
        ? currentTransform - slideDistance
        : currentTransform + slideDistance

    containerRef.current.style.transform = `translateX(${targetTransform}px)`

    if (animationRef.current) clearTimeout(animationRef.current)

    animationRef.current = setTimeout(() => {
      resetContainer(direction)
      setIsAnimating(false)
    }, 500)
  }

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current)
      }
    }
  }, [])

  const handleNext = () => slide("next")
  const handlePrev = () => slide("prev")

  setControls({
    prev: handlePrev,
    next: handleNext,
  })

  return (
    <div className="relative flex-row overflow-hidden">
      <div className="overflow-hidden relative w-full">
        <div
          ref={containerRef}
          className="flex flex-row transition-transform duration-500 ease-linear gap-[20px]"
        >
          {cards.map((card, index) => (
            <div
              key={`${card._id}-${index}`}
              ref={(el) => (cardRefs.current[index] = el)}
            >
              {itemRender({ card, index })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Update NavButtonContainer to handle disabled state
const NavButtonContainer = ({ getControls, disabled }) => (
  <div className="flex z-20 justify-start mt-6 gap-[40px] mx-auto">
    <NavigationButton
      direction="prev"
      onClick={() => getControls().prev()}
      disabled={disabled}
    />
    <NavigationButton
      direction="next"
      onClick={() => getControls().next()}
      disabled={disabled}
    />
  </div>
)

const UserCard = ({ card, index, bgColor }) => {
  const rating = card.rating?.toString()?.replace(".", ",")

  return (
    <div
      id={card._id}
      key={`user-card-${index}`}
      className={twMerge(
        "flex flex-col justify-start w-[315px] md:w-[422px] lg:w-[480px] h-[570px] lg:h-[620px] xl:h-[630px] px-6 md:px-10 py-8 xl:py-8 rounded-[50px] shadow-sm",
        bgColor
      )}
    >
      {/* Rating Section */}
      <div className="w-full h-[85px] md:h-[100px] xl:h-[110px] flex-shrink-0 border-b border-bordered mb-6 lg:mb-7">
        <Typography
          component="h3"
          variant="h2"
          className="text-left text-text mb-[16px]"
        >
          {rating}
        </Typography>

        {/* Rating Stars */}
        <div className="flex items-center">
          <img src={card.rating_image} alt="stars" className="h-5" />
        </div>
      </div>

      {/* Content Section */}
      <div className="flex-col justify-start">
        <Typography
          component="h4"
          variant="h3"
          className="text-left text-text mb-3"
        >
          {card.title}
        </Typography>

        <Typography variant="body1" className="text-left mb-[20px]">
          {card.body}
        </Typography>
      </div>

      {/* User Info Section */}
      <div className="w-full h-[105px] flex flex-row items-center gap-[20px] mt-auto">
        <img
          src={card.user_icon}
          alt={card.username}
          className="w-[105px] h-[105px] rounded-full object-cover flex-shrink-0"
        />

        <div className="flex flex-col">
          <Typography
            variant="body1_accent"
            className="text-left mt-0 mb-[10px]"
          >
            {card.username}
          </Typography>

          {card.occupation && card.occupation.trim() !== "" && (
            <Typography variant="txt1_dim" className="text-left">
              {card.occupation}
            </Typography>
          )}

          <div className="text-left">
            {card.store_icon?.[0]?.image?.[0] && (
              <img
                src={card.store_icon?.[0]?.image?.[0]}
                alt="App Store"
                className="h-[28px] w-auto object-contain mt-[6px]"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const UserRatingsSection = ({ section, id, bgColor }) => {
  const controls = { prev: () => {}, next: () => {} }
  const setControls = (newControls) => {
    controls.prev = newControls.prev
    controls.next = newControls.next
  }
  const getControls = () => controls
  return (
    <div key={id} className="w-full text-center mx-auto">
      <Typography
        component="h2"
        variant="h1"
        className="max-w-[90%] text-text text-center mb-[58px] lg:mb-[96px]"
      >
        {section.title}
      </Typography>

      <div className="flex items-center lg:gap-[32px]">
        {/* Left part: Guy with Laptop / Lady with phone, Heading, Rating */}
        <div className="hidden lg:flex flex-col relative lg:min-w-[380px] xl:min-w-[420px] lg:max-w-[460px] items-start bg-paper">
          {/* Background Image and Gradient */}
          <div className="relative overflow-hidden rounded-lg w-full h-full ">
            <img
              src={section.user_image}
              alt="Background"
              className="object-contain lg:h-[460px] xl:h-[480px] aspect-529/560 lg:pb-16"
            />

            <div
              className="absolute inset-0 bg-gradient-to-t from-paper via-white/80 to-transparent"
              style={{ height: "80%", top: "10%" }}
            />
          </div>

          {/* Content */}
          <div className="absolute bottom-0 left-0 lg:w-[340px] xl:w-[390px] text-left pr-4 z-10">
            <div className="flex items-center gap-[16px]">
              <img
                src={section.star_image}
                alt="Star"
                className="w-[40px] h-[40px]"
              />

              <Typography component="h2" variant="h1" className="text-text">
                {section.rating}
              </Typography>
            </div>

            {/* AppStore Icon */}
            <DynamicSVG
              url={section.store_image}
              className="w-[148px] h-[40px] text-text mt-[30px] mb-[40px]"
            />

            {/* User Review */}
            <Typography variant="h3" className="text-text mb-[48px]">
              {section.user_review}
            </Typography>

            {/* Navigation Buttons */}
            <div className="hidden md:block">
              <NavButtonContainer getControls={getControls} disabled={false} />
            </div>
          </div>
        </div>

        {/* Cards Carousel */}
        <div className="w-full flex">
          <Carousel
            setControls={setControls}
            items={section.card}
            itemRender={({ card, index }) => (
              <UserCard card={card} index={index} bgColor={bgColor} />
            )}
          />
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex lg:hidden mx-auto mt-[34px]">
        <NavButtonContainer getControls={getControls} disabled={false} />
      </div>
    </div>
  )
}

const TestimonialsSection = ({ tid, bgColor }) => {
  return (
    <div className="w-full pl-[16px] md:pl-px1_l xl:pl-px1_xl py-[58px] lg:py-[116px] overflow-hidden">
      <CMSRender
        contentType="UserAppRatingSection"
        match={{ "content.id": tid }}
        renderItem={(section, id) => (
          <UserRatingsSection section={section} id={id} bgColor={bgColor} />
        )}
      />
    </div>
  )
}

export default TestimonialsSection
